import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { colors, fontSize, fontWeight, radius, space } from "../../lib/tokens";
import { SkeletonBox } from "../Skeleton";
import { EmptyState } from "../EmptyState";

type Range = "12m" | "6m" | "3m";

interface Snapshot {
  date: string;
  total_mrr: number;
}

export function MrrChart() {
  const [range, setRange] = useState<Range>("12m");
  const [data, setData] = useState<Snapshot[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const months = range === "12m" ? 12 : range === "6m" ? 6 : 3;
    const from = new Date();
    from.setMonth(from.getMonth() - months);
    supabase
      .from("platform_mrr_snapshots")
      .select("date, total_mrr")
      .gte("date", from.toISOString().slice(0, 10))
      .order("date", { ascending: true })
      .then(({ data: rows, error }) => {
        if (!error && rows) setData(rows as Snapshot[]);
        setLoading(false);
      });
  }, [range]);

  const max = Math.max(...data.map((d) => Number(d.total_mrr)), 1);
  const W = 800;
  const H = 200;
  const P = 24;

  const pts = data.map((d, i) => {
    const x = P + (i / Math.max(data.length - 1, 1)) * (W - 2 * P);
    const y = H - P - (Number(d.total_mrr) / max) * (H - 2 * P);
    return `${x},${y}`;
  });
  const path = pts.length ? `M ${pts.join(" L ")}` : "";
  const areaPath = pts.length ? `${path} L ${P + (W - 2 * P)},${H - P} L ${P},${H - P} Z` : "";

  return (
    <div
      style={{
        background: colors.white,
        border: `1px solid ${colors.fog}`,
        borderRadius: radius.md,
        padding: space.lg,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: space.md,
        }}
      >
        <h3 style={{ margin: 0, fontSize: fontSize.base, color: colors.ink, fontWeight: fontWeight.semibold }}>MRR</h3>
        <div style={{ display: "flex", gap: 4 }}>
          {(["3m", "6m", "12m"] as Range[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              style={{
                border: "none",
                padding: "4px 10px",
                borderRadius: radius.xs,
                background: range === r ? colors.indigo : "transparent",
                color: range === r ? colors.white : colors.stone,
                cursor: "pointer",
                fontSize: fontSize.xs,
                fontWeight: fontWeight.medium,
              }}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <SkeletonBox height={H} />
      ) : data.length === 0 ? (
        <EmptyState>Nenhum dado de MRR ainda</EmptyState>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H }}>
          <path d={areaPath} fill="rgba(61,78,232,0.12)" />
          <path d={path} fill="none" stroke={colors.indigo} strokeWidth={2} />
          {pts.map((pt, i) => {
            const [x, y] = pt.split(",").map(Number);
            return <circle key={i} cx={x} cy={y} r={3} fill={colors.indigo} />;
          })}
        </svg>
      )}
    </div>
  );
}
